import { z } from 'zod';
/**
 * Version minimale des apps mobiles (iOS, Android).
 *
 * Plusieurs versions des apps coexistent ; l'API `/v1` ne reçoit que des
 * ajouts. Quand une version devient dangereuse ou trop ancienne, la console
 * relève la version MINIMALE de sa plateforme : l'app la lit dans
 * `GET /v1/apps/version` et se bloque, et l'API répond 426
 * `app.mise_a_jour_requise` à toute requête qui se présente plus ancienne
 * (`X-Artosera-App`). La version CONSEILLÉE n'ajoute qu'un bandeau.
 */
export const PLATEFORMES_APP = ['ios', 'android'];
/** `X`, `X.Y` ou `X.Y.Z`, entiers sans zéro de tête. */
const MOTIF_VERSION = /^(0|[1-9]\d{0,5})(\.(0|[1-9]\d{0,5})){0,2}$/;
export const versionAppSchema = z
    .string()
    .trim()
    .regex(MOTIF_VERSION, 'Version attendue : 1, 1.2 ou 1.2.3.');
/** Réglage d'une plateforme dans la console : tout est remplacé d'un geste. */
export const exigencesPlateformeSchema = z.strictObject({
    minimale: versionAppSchema.nullable(),
    conseillee: versionAppSchema.nullable(),
    urlBoutique: z
        .url({ protocol: /^https$/ })
        .max(500)
        .nullable(),
});
/** La conseillée ne descend pas sous la minimale : à poser en `refine`. */
export const CONSEILLEE_COHERENTE = [
    (e) => e.minimale === null || e.conseillee === null || comparerVersions(e.minimale, e.conseillee) <= 0,
    {
        message: 'La version conseillée ne peut pas être inférieure à la minimale.',
        path: ['conseillee'],
    },
];
/** Les composantes d'une version, ou `null` si elle est illisible. */
function composantes(version) {
    if (!MOTIF_VERSION.test(version))
        return null;
    return version.split('.').map(Number);
}
/**
 * Compare deux versions lisibles composante par composante, une composante
 * absente valant 0 (`1.2` = `1.2.0`). Négatif si `a < b`.
 */
export function comparerVersions(a, b) {
    const x = composantes(a) ?? [];
    const y = composantes(b) ?? [];
    for (let i = 0; i < 3; i++) {
        const ecart = (x[i] ?? 0) - (y[i] ?? 0);
        if (ecart !== 0)
            return ecart;
    }
    return 0;
}
/** En-tête que les apps posent sur chaque requête : `ios/1.2.3 (45)`. */
export const ENTETE_APP = 'x-artosera-app';
/** Lit `X-Artosera-App` ; `null` s'il est absent ou illisible (régie web, scripts). */
export function lireEnteteApp(valeur) {
    const lu = /^(ios|android)\/(\S+)(?: \((\d{1,10})\))?$/.exec((valeur ?? '').trim());
    if (!lu || composantes(lu[2]) === null)
        return null;
    return { plateforme: lu[1], version: lu[2], build: lu[3] ?? null };
}
/** L'app se présente-t-elle sous la version minimale de sa plateforme ? */
export function sousLaMinimale(identite, exigences) {
    const minimale = exigences[identite.plateforme].minimale;
    return minimale !== null && comparerVersions(identite.version, minimale) < 0;
}
//# sourceMappingURL=versions-apps.js.map